import React, {useState} from 'react'
import {Button, Input} from 'react-daisyui'
import {confirmResetPassword} from 'aws-amplify/auth'
import LoadingSpinner, {LoadingSpinnerSize} from '../common/LoadingSpinner'
import clsx from 'clsx'
import {useForm} from 'react-hook-form'
import {
  trackFormSubmission,
  trackValidationError
} from '../../services/errorAnalytics'

export const ConfirmPasswordResetForm = ({
  username,
  onConfirmed
}: {
  username: string
  onConfirmed?: Function
}) => {
  const {
    register,
    handleSubmit,
    formState: {errors},
    watch
  } = useForm()
  const [isConfirming, setIsConfirming] = useState(false)

  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Track validation errors
  const trackFieldValidationError = (
    fieldName: string,
    errorMessage: string
  ) => {
    trackValidationError('password_reset_confirm', fieldName, errorMessage)
  }

  const handleConfirmCode = async (data: any): Promise<void> => {
    setIsConfirming(true)
    try {
      await confirmResetPassword({
        username,
        newPassword: data.newPassword,
        confirmationCode: data.verificationCode
      })

      // Track successful form submission
      trackFormSubmission('password_reset_confirm', true)

      if (onConfirmed) {
        onConfirmed()
      }
    } catch (error) {
      const errorMessage = error.message || 'An error occurred'

      // Track failed form submission
      trackFormSubmission('password_reset_confirm', false, errorMessage)

      setErrorMessage(errorMessage)
    } finally {
      setIsConfirming(false)
    }
  }

  return (
    <form
      className="grid grid-cols-1 gap-2"
      onSubmit={handleSubmit(handleConfirmCode)}
    >
      {errorMessage && <div className="text-red-500">{errorMessage}</div>}
      <div>
        <label
          className={
            'label ' + (errors.verificationCode ? ' text-red-error' : '')
          }
        >
          Verification Code
        </label>
        <Input
          {...register('verificationCode', {required: true})}
          name="verificationCode"
          className={
            'input-form' + (errors.verificationCode ? ' input-form-error' : '')
          }
          onBlur={e => {
            if (errors.verificationCode?.type === 'required') {
              trackFieldValidationError(
                'verificationCode',
                'Verification code is required'
              )
            }
          }}
        ></Input>
        {errors.verificationCode?.type === 'required' && (
          <p role="alert">Verification code is required</p>
        )}
      </div>
      <div>
        <label
          className={'label ' + (errors.newPassword ? ' text-red-error' : '')}
        >
          New Password
        </label>

        <Input
          {...register('newPassword', {required: true})}
          name="newPassword"
          type="password"
          className={
            'input-form' + (errors.newPassword ? ' input-form-error' : '')
          }
          onBlur={e => {
            if (errors.newPassword?.type === 'required') {
              trackFieldValidationError(
                'newPassword',
                'New password is required'
              )
            }
          }}
        ></Input>
        {errors.newPassword?.type === 'required' && (
          <p role="alert">New password is required</p>
        )}
      </div>
      <div>
        <label
          className={
            'label ' + (errors.confirmPassword ? ' text-red-error' : '')
          }
        >
          Confirm Password
        </label>

        <Input
          {...register('confirmPassword', {
            required: true,
            validate: (val: string) => {
              if (watch('newPassword') !== val) {
                return 'match'
              }
            }
          })}
          name="confirmPassword"
          type="password"
          className={
            'input-form' + (errors.confirmPassword ? ' input-form-error' : '')
          }
          onBlur={e => {
            if (errors.confirmPassword?.type === 'required') {
              trackFieldValidationError(
                'confirmPassword',
                'Confirm password is required'
              )
            } else if (errors.confirmPassword?.type === 'match') {
              trackFieldValidationError(
                'confirmPassword',
                'Passwords do not match'
              )
            }
          }}
        ></Input>
        {errors.confirmPassword?.type === 'match' && (
          <p role="alert">Passwords do not match</p>
        )}
      </div>
      <Button
        type="submit"
        className="bg-cabana-500 btn-secondary disabled:text-white w-full mt-4"
      >
        Confirm
      </Button>
      <div className={clsx({hidden: !isConfirming})}>
        <div className="flex flex-row justify-center items-center pt-4 gap-2">
          <LoadingSpinner size={LoadingSpinnerSize.Large} />
        </div>
      </div>
    </form>
  )
}

export default ConfirmPasswordResetForm
